import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const links = [
  { to: "/wheels", label: "Wheels" },
  { to: "/apparel", label: "Apparel" },
  { to: "/gallery", label: "Builds" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobile(false); }, [location.pathname]);

  return (
    <header data-testid="navbar" className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass border-b border-white/10" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="h-2 w-2 bg-adrenaline animate-pulse-red rounded-full" />
          <span className="text-display text-2xl md:text-3xl tracking-wider text-white">LONE STAR <span className="text-adrenaline">SPEED</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) => `text-heading uppercase text-sm tracking-[0.15em] transition-colors ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user?.is_admin && (
            <Link to="/admin" data-testid="admin-link" className="hidden md:inline text-mono uppercase text-xs tracking-[0.2em] text-adrenaline hover:text-white transition-colors">Admin</Link>
          )}
          {user ? (
            <div className="hidden md:flex items-center gap-2 text-zinc-300">
              <User size={16} /><span className="text-mono text-xs">{user.name}</span>
            </div>
          ) : (
            <Link to="/login" data-testid="login-link" className="hidden md:inline text-mono uppercase text-xs tracking-[0.2em] text-zinc-300 hover:text-white">Login</Link>
          )}
          <button data-testid="open-cart-btn" onClick={() => setOpen(true)} className="relative p-2 text-white hover:text-adrenaline transition-colors">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-adrenaline text-white text-[10px] font-mono px-1.5 py-0.5 rounded-full leading-none">{count}</span>
            )}
          </button>
          <button data-testid="mobile-menu-btn" className="md:hidden p-2 text-white" onClick={() => setMobile(!mobile)}>
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {mobile && (
        <div data-testid="mobile-menu" className="md:hidden glass border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="text-heading uppercase text-base tracking-[0.15em] text-white">{l.label}</Link>
            ))}
            {user?.is_admin && <Link to="/admin" className="text-mono uppercase text-xs text-adrenaline">Admin</Link>}
            {!user && <Link to="/login" className="text-mono uppercase text-xs text-zinc-300">Login</Link>}
          </div>
        </div>
      )}
    </header>
  );
}
